import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import type { PipelineActor } from '@/versions/v1/modules/advisory/PipelineStrip.transform';
import type { KpiTile, RoleTable } from '@/versions/v1/schema/plant-role';
import type { PlantCadence, SampleAdherence, SampleSource } from '@/versions/v1/schema/unit-operation';
import { parseSampleAdherence } from '@/versions/v1/shared/StateChip';
import {
  ARSENIC_TREND_30D,
  AS_DISTRIBUTION_BY_PRODUCT,
  EXTRA_ROLE_TABLES,
  ROLE_KPIS,
  ROLE_TABLES,
  SAMPLE_SOURCES,
  SETPOINT_IN_RANGE,
  type BunkerHillMeterDatum,
  type BunkerHillTrendBundle,
} from '@/versions/v1/shared/bunker-hill-seed';
import { CASES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { arsenicContractLimit } from '@/versions/v1/shared/bunker-hill-concentrate-specs';
import {
  displayedClosureGapPct,
  isPeriodMeasured,
  periodView,
} from '@/versions/v1/modules/ingestion/periodBalance';
import { closureCloses, displayedGapPct } from '@/versions/v1/modules/ingestion/MetalAccountingLayer.transform';
import { concentratePenaltyPanelModel } from './ConcentratePenaltyPanel.transform';

export type QualityTab = 'sample-register' | 'impurities' | 'samples-due';

export type QualityCopy = {
  title: string;
};

export type SampleDueItem = {
  id: string;
  stream: string;
  slot: string;
  state: SampleAdherence;
  note?: string;
  caseId?: string;
  /** All Samples Due rows are collection tasks in v1. */
  taskKind: 'collect-sample';
  /** True for a campaign's upcoming-slot rows (SA-F19): nested under the
   * campaign banner rather than a sibling row, so the stream name is not
   * repeated once per slot. */
  continuation?: boolean;
  /** A campaign banner row's own interval and run length, carried structured
   *  from the request so `samplePipelineRead()` reads the campaign's cadence
   *  off the row instead of parsing its slot text as a clock time. */
  campaign?: { intervalMin: number; hours: number };
};

export type SampleDueLists = {
  dueNow: SampleDueItem[];
  collectedPending: SampleDueItem[];
  missed: SampleDueItem[];
};

export type CrewTrendRow = {
  id: string;
  day: string;
  overallPct: string;
};

const ALL_TABLES: readonly RoleTable[] = [...ROLE_TABLES, ...EXTRA_ROLE_TABLES];

export const QUALITY_TABS: { id: QualityTab; label: string }[] = [
  { id: 'sample-register', label: 'Sample Register & Lab' },
  { id: 'impurities', label: 'Impurities' },
  { id: 'samples-due', label: 'Samples Due' },
];

const TAB_TO_SEED: Record<Exclude<QualityTab, 'samples-due'>, string> = {
  'sample-register': 'samples',
  impurities: 'impurities',
};

/** The page title; the rail names the module, so there is no description line. */
export function qualityCopy(): QualityCopy {
  return { title: 'Quality & Lab' };
}

export function kpisForQualityTab(tab: Exclude<QualityTab, 'samples-due'>): KpiTile[] {
  const tabId = TAB_TO_SEED[tab];
  return ROLE_KPIS.filter((kpi) => kpi.roleId === 'metallurgist' && kpi.tabId === tabId);
}

export function tablesForQualityTab(tab: Exclude<QualityTab, 'samples-due'>): RoleTable[] {
  const tabId = TAB_TO_SEED[tab];
  return ALL_TABLES.filter((table) => table.roleId === 'metallurgist' && table.tabId === tabId);
}

/** ~55-day penalty projection from the arsenic case observation (B: Impurities). */
export function impuritiesPenaltyLine(): string {
  const caseItem = CASES.find((row) => row.id === 'case-as-trend');
  if (!caseItem?.observation) {
    return `At +0.003%/day the ${arsenicContractLimit('Pb').toFixed(2)}% penalty threshold is reached in ~55 days.`;
  }
  return caseItem.observation;
}

export const IMPURITIES_ACTION = {
  headline: 'Investigate ore source — is mining advancing into a higher-As zone?',
  detail:
    'Consider depressing arsenopyrite: Pb pH 8.4 → 7.8–8.0, or more SMBS. Request ROM + blast As assays.',
  caseId: 'case-as-trend',
} as const;

/** Due-now / collected-pending / missed lists from deck sample grids + SAMPLE_POINTS. */
function samplesDueLiteralLists(): SampleDueLists {
  return {
    dueNow: [
      { id: 'due-1', stream: 'ROM Head (feed)', slot: '12:00', state: 'DUE', taskKind: 'collect-sample' },
      { id: 'due-2', stream: 'Pb/Ag Concentrate', slot: '12:00', state: 'DUE', taskKind: 'collect-sample' },
      { id: 'due-3', stream: 'Pb Tails / Zn Feed', slot: '12:00', state: 'DUE', taskKind: 'collect-sample' },
      { id: 'due-4', stream: 'Zn Concentrate', slot: '12:00', state: 'DUE', taskKind: 'collect-sample' },
      { id: 'due-5', stream: 'Final Tails', slot: '12:00', state: 'DUE', taskKind: 'collect-sample' },
    ],
    collectedPending: [
      { id: 'pend-1', stream: 'Final Tails', slot: '10:00', state: 'PENDING', taskKind: 'collect-sample' },
      { id: 'pend-2', stream: 'ZnSO₄ Solution', slot: '08:00', state: 'PENDING', taskKind: 'collect-sample' },
      { id: 'pend-3', stream: 'SMBS Solution', slot: '08:00', state: 'PENDING', taskKind: 'collect-sample' },
    ],
    missed: [
      {
        id: 'miss-1',
        stream: 'Zn Rougher Conc',
        slot: '10:00',
        state: 'MISS',
        note: 'reason: operator on pH duty',
        caseId: 'case-zn-recovery',
        taskKind: 'collect-sample',
      },
    ],
  };
}

/** Whether a register-grid Status cell should render as a seven-state chip. */
export function isRegisterStatusColumn(table: RoleTable, columnIndex: number): boolean {
  return (
    table.caption.startsWith('Sample register') && table.columns[columnIndex] === 'Status'
  );
}

/** Whether a shift-style hourly grid column should render adherence chips. */
export function isHourlyGridColumn(table: RoleTable, columnIndex: number): boolean {
  if (columnIndex === 0) return false;
  return (
    table.caption.startsWith('Sample tracking') ||
    table.caption.startsWith('Operator rounds')
  );
}

/** Case drawer target for a sample-register row Status cell. */
export function registerStatusCaseId(rowId: string, cell: string): string | undefined {
  if (rowId === 'sr-7' && parseSampleAdherence(cell) === 'MISS') {
    return 'case-zn-recovery';
  }
  return undefined;
}

/** SA-F21: under `osa`, project the register's Frequency (and latency
 * class) for every analyser-covered point through the same `OSA_CADENCE`
 * table `samplesDueLists` already uses, so the register agrees with Samples
 * Due about the same points instead of only ever showing the seeded lab
 * cadence. The seed itself (`SAMPLE_SOURCES`) is untouched -- this is a
 * projection, exactly like the TopBar LIMS chip. */
export function sampleSourceInventory(cadence?: PlantCadence): readonly SampleSource[] {
  if (cadence !== 'osa') return SAMPLE_SOURCES;
  return SAMPLE_SOURCES.map((source) => {
    if (!source.samplePointId || !OSA_CADENCE[source.samplePointId]) return source;
    // SA-F26: an analyser-covered source under OSA is collected by the
    // analyser, not manually -- `method` must agree with `frequency` /
    // `latencyClass` on the same projected object.
    return { ...source, frequency: '30 min · analyser', latencyClass: 'online', method: 'analyser' };
  });
}

/** SA-F23: under `osa`, the Sample Register & Lab tab's own caption and
 * per-row Freq/Method cells must agree with the inventory panel that sits
 * 58px above it (`sampleSourceInventory`) -- not assert "all manual, no
 * auto-samplers" while an analyser row from the same projection is on
 * screen. The RoleTable seed stays frozen; this is a projection, exactly
 * like `sampleSourceInventory` and the TopBar LIMS chip. Reuses
 * `OSA_CADENCE` (the one source of truth for analyser coverage) and a
 * register-row -> sample-point identity map in the same shape as
 * `DUE_ROW_SAMPLE_POINT` below -- the deck's register-row stream names
 * differ from the `SAMPLE_POINTS` stream names for the same physical
 * point. Rows with no mapped sample point (ROM head, regrind, reagents,
 * utilities) are untouched -- they were never claimed to be on an
 * analyser. Under `lab` (or no cadence) the seeded table is returned
 * unchanged. */
const REGISTER_ROW_SAMPLE_POINT: Record<string, string> = {
  'sr-2': 'SA03', // Pb/Ag concentrate
  'sr-3': 'SA07', // Pb tails / Zn feed
  'sr-4': 'SA04', // Zn concentrate
  'sr-5': 'SA05', // Final tails
  'sr-6': 'SA06', // Pb rougher concentrate
  'sr-7': 'SA08', // Zn rougher conc
};

export function projectRegisterTable(table: RoleTable, cadence?: PlantCadence): RoleTable {
  if (cadence !== 'osa' || !table.caption.startsWith('Sample register')) return table;
  const freqIdx = table.columns.indexOf('Freq');
  const methodIdx = table.columns.indexOf('Method');
  const rows = table.rows.map((row) => {
    const samplePointId = REGISTER_ROW_SAMPLE_POINT[row.id];
    if (!samplePointId || !OSA_CADENCE[samplePointId]) return row;
    const cells = [...row.cells];
    if (freqIdx >= 0) cells[freqIdx] = '30 min';
    if (methodIdx >= 0) cells[methodIdx] = 'Analyser';
    return { ...row, cells };
  });
  return {
    ...table,
    caption: `Sample register · today vs LIMS (${osaCoverageLabel('osa')})`,
    rows,
  };
}

/**
 * One phrase for analyser coverage, derived from `OSA_CADENCE`, so every
 * surface that names the cadence (register caption, plant-model sample
 * latency strip) says the same thing and flips together.
 */
export function osaCoverageLabel(cadence?: PlantCadence): string {
  if (cadence !== 'osa') return 'All manual · no OSA';
  const covered = Object.entries(OSA_CADENCE)
    .filter(([, on]) => on)
    .map(([id]) => id.replace(/^SA/, ''));
  return `OSA analysers on SA${covered.join('/')} · balance manual`;
}

export function sampleSourceCount(): number {
  return SAMPLE_SOURCES.length;
}

/** B: slide 7 — red < 70 · amber 70–89 · green ≥ 90. */
export function setpointInRangeTone(pct: number): VizTone {
  if (pct >= 90) return 'ok';
  if (pct >= 70) return 'warn';
  return 'bad';
}

/** B: slide 7 — setpoint %-in-range meter rows with deck tone bands. */
export function setpointMetersForRegister(): readonly BunkerHillMeterDatum[] {
  return SETPOINT_IN_RANGE.map((row) => ({
    ...row,
    tone: setpointInRangeTone(row.value),
  }));
}

export type MassBalanceMeter = {
  id: string;
  metal: string;
  errorPct: number;
  errorLabel: string;
  status: string;
  statusTone: VizTone;
};

/** The widget's metals, in the surface's order (Zn · Pb · Ag). */
const MASS_BALANCE_METALS: readonly { id: string; metal: 'Zn' | 'Pb' | 'Ag' }[] = [
  { id: 'mb-zn', metal: 'Zn' },
  { id: 'mb-pb', metal: 'Pb' },
  { id: 'mb-ag', metal: 'Ag' },
];

/** B: slide 7 — Zn, Pb and Ag closure bars (±3% tolerance).
 *
 *  F1 (#727): the gap is the engine's displayed-precision closure for the day
 *  — `displayedClosureGapPct` over the day `PeriodBalance`, the same read
 *  Metal accounting's closure panel prints — not the retired typed seed rows.
 *  The verdict reads the one K17 threshold function (`closureCloses`), so the
 *  chip and the tone cannot disagree with Metal accounting. */
export function massBalanceMeters(): MassBalanceMeter[] {
  const view = periodView('day');
  if (!isPeriodMeasured(view)) return [];

  return MASS_BALANCE_METALS.map(({ id, metal }) => {
    // The bar and the label both read the displayed (1 dp) gap, the figure the
    // threshold is evaluated on.
    const errorPct = displayedGapPct(displayedClosureGapPct(view, metal));
    const closes = closureCloses(errorPct);
    return {
      id,
      metal,
      errorPct,
      errorLabel: `${errorPct > 0 ? '+' : errorPct < 0 ? '−' : ''}${Math.abs(errorPct).toFixed(1)}%`,
      status: closes ? 'CLOSED' : 'REVIEW',
      statusTone: closes ? 'ok' : 'warn',
    };
  });
}

export type ArsenicCircuitNode = {
  id: string;
  name: string;
  line1: string;
  line2: string;
  tone?: VizTone;
};

/** B: slide 8 — arsenic circuit distribution (nine nodes). */
export const ARSENIC_CIRCUIT_NODES: readonly ArsenicCircuitNode[] = [
  { id: 'as-n1', name: 'ROM feed', line1: '0.12% As', line2: '100% of As' },
  { id: 'as-n2', name: 'Ball mill', line1: 'P80 82 µm', line2: 'Liberated' },
  { id: 'as-n3', name: 'Pb rougher', line1: 'pH 8.4', line2: 'As partly floats' },
  {
    id: 'as-n4',
    name: 'Pb/Ag conc',
    line1: '0.32% As',
    line2: '~7% of As',
    tone: 'warn',
  },
  { id: 'as-n5', name: 'Zn rougher', line1: 'pH 10.8', line2: 'Cu activation' },
  {
    id: 'as-n6',
    name: 'Zn conc',
    line1: '0.06% As',
    line2: '~4% of As',
    tone: 'ok',
  },
  { id: 'as-n7', name: 'Regrind + clean', line1: 'P80 30 µm', line2: 'Conc regrind' },
  { id: 'as-n8', name: 'Final tails', line1: '0.10% As', line2: '~89% of As → dam', tone: 'ok' },
  { id: 'as-n9', name: 'Paste', line1: 'Backfill', line2: 'Encapsulate' },
];

// SMELTER_LIMITS_PROVENANCE removed paul-fmea.A15 / #280 — the Impurities
// panel now reads `ConcentrateSpec` (`ConcentratePenaltyPanel`), which
// carries contract-limit and typical-trigger provenance per element rather
// than one inferred line for a literal table.

export const ARSENIC_TREND_SYN_NOTE = '[syn] Illustrative 30-day trend';

export function arsenicTrend30d(): BunkerHillTrendBundle {
  return ARSENIC_TREND_30D;
}

export function asDistributionMeters(): readonly BunkerHillMeterDatum[] {
  return AS_DISTRIBUTION_BY_PRODUCT;
}

/** Banner copy tied to `case-as-trend` — do not retype on the glass. */
export function impuritiesActionBanner(): { text: string; caseId: string } {
  return {
    text: `${IMPURITIES_ACTION.headline} ${IMPURITIES_ACTION.detail}`,
    caseId: IMPURITIES_ACTION.caseId,
  };
}

/** Sampling-artefact gate line for the As trend panel (emails.A18). */
export function arsenicArtefactCheckLine(): string | null {
  const caseItem = CASES.find((row) => row.id === 'case-as-trend');
  if (!caseItem?.samplingArtefactCheck) return null;
  return `${caseItem.samplingArtefactCheck.text} · lab due ${caseItem.samplingArtefactCheck.labDueAt}`;
}

export type ReagentSupplyMeter = {
  id: string;
  /** Reagent name (`Aero 5100`). */
  name: string;
  /** The seed's descriptor (`Pb depressant`); empty when the seed note is the
   *  REORDER status, which the bar and the tick already draw. */
  role: string;
  days: number;
  valueLabel: string;
  tone: VizTone;
};

/** Days-of-supply reorder point: the tick on every reagent bar. */
export const REAGENT_REORDER_DAYS = 7;
/** Days-of-supply bar scale. */
export const REAGENT_SCALE_DAYS = 30;

/** At or below the reorder point. One predicate behind both the bar's `bad`
 *  tone and the "below reorder" count, so the two can never disagree
 *  (issue #658 round 2: the tone used `<=`, the count `<`). */
export function belowReorder(days: number): boolean {
  return days <= REAGENT_REORDER_DAYS;
}

/** Days-of-supply on a 30-day scale; at/below the reorder point (bad), ≤14d watch (warn). */
export function reagentSupplyMeters(table: RoleTable): ReagentSupplyMeter[] {
  return table.rows.map((row) => {
    const [name, daysCell, note] = row.cells;
    const days = Number.parseInt(daysCell ?? '', 10) || 0;
    const tone: VizTone = belowReorder(days) ? 'bad' : days <= 14 ? 'warn' : 'ok';
    return {
      id: row.id,
      name: name ?? row.id,
      role: note && note !== 'REORDER' ? note : '',
      days,
      valueLabel: `${days} d`,
      tone,
    };
  });
}

// --- route-reads samples (paul-call.A8 #256) ----------------------------------
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { inBandDivergences, samplePointForDivergence } from '@/versions/v1/shared/SpcEngine';
import { addMinutes, parseTime, formatTime } from '@/versions/v1/shared/clock.derive';
import { assigneeLabel } from '@/versions/v1/modules/advisory/Scene.transform';

/** Which SAMPLE_POINTS read on the on-stream analyser under `osa` cadence
 * (S2.1). Slurry streams ahead of the lab (SA01/02/04/06/07/08) move to the
 * analyser at 30 min; the Pb/Ag final-concentrate composite (SA03) and final
 * tailings (SA05) stay lab -- both are contractual/discharge composites the
 * deck never puts on an analyser loop. Consumed by `samplesDueLists` below to
 * recompute the due clock of every literal `dueNow` row that maps to an
 * analyser-covered sample point (S2.2). */
export const OSA_CADENCE: Record<string, boolean> = {
  SA01: true,
  SA02: true,
  SA03: false,
  SA04: true,
  SA05: false,
  SA06: true,
  SA07: true,
  SA08: true,
};

/** The literal `dueNow` rows (deck slide, S6) map to `SAMPLE_POINTS` register
 * entries by stream identity, not by name -- the deck's slide names differ
 * from the register's stream names for the same physical point. Used to
 * decide which literal rows recompute their clock under `osa` (S2.2). */
const DUE_ROW_SAMPLE_POINT: Record<string, string> = {
  'due-2': 'SA03', // Pb/Ag Concentrate
  'due-3': 'SA07', // Pb Tails / Zn Feed
  'due-4': 'SA04', // Zn Concentrate
  'due-5': 'SA05', // Final Tails
};

/** Last 30-min analyser slot at or before the cursor, e.g. 11:47 -> 11:30. */
function lastHalfHourSlot(cursor: string): string {
  const d = parseTime(cursor);
  const minutes = d.getUTCMinutes() >= 30 ? 30 : 0;
  d.setUTCMinutes(minutes, 0, 0);
  return formatTime(d);
}

function clockOf(at: string): string {
  return at.split('T')[1] ?? at;
}

/** Due-now / collected-pending / missed lists (S2.2). The lab-cadence base
 * rows are the seeded literals -- a byte-identical acceptance bar the probe
 * checks -- kept as a documented exception (frequency-string parsing cannot
 * reproduce the deck's hand-set slot times without re-authoring the deck).
 * Under `osa` the literal rows whose sample point is analyser-covered
 * (`OSA_CADENCE`) recompute to the last 30-min slot at/before the cursor, so
 * switching cadence visibly moves due times even for the seeded rows. The
 * campaign/rush projection reads the *current* `state.cadence`, not the
 * mode recorded on the request at click time (S2.3/S1.4 residue) -- so a
 * request made under `lab` renders as a campaign the moment the plant
 * switches to `osa`, and back. */
export function samplesDueLists(
  state?: SessionState,
  advisory?: AdvisorySessionState,
): SampleDueLists {
  const base = samplesDueLiteralLists();
  if (!state || !advisory) return base;

  const isOsa = state.cadence === 'osa';
  let dueNow = base.dueNow.map((item) => {
    const samplePointId = DUE_ROW_SAMPLE_POINT[item.id];
    if (!isOsa || !samplePointId || !OSA_CADENCE[samplePointId]) return item;
    const slot = clockOf(lastHalfHourSlot(state.cursor));
    return { ...item, slot, note: `${samplePointId} · 30 min (analyser online)` };
  });

  const divergences = inBandDivergences(state);
  for (const divergence of divergences) {
    const request = advisory.sampleRequests.get(divergence.id);
    if (!request) continue;
    const samplePoint = samplePointForDivergence(divergence);
    const streamLabel = samplePoint ? samplePoint.stream : divergence.readingId;
    const shownClock = clockOf(request.requestedAt);
    const requesterLabel = assigneeLabel(request.requestedBy);
    if (isOsa) {
      const intervalMin = request.campaign?.intervalMin ?? 30;
      const hours = request.campaign?.hours ?? 4;
      const banner: SampleDueItem = {
        id: `campaign-${divergence.id}`,
        stream: `Campaign · ${streamLabel}`,
        slot: `every ${intervalMin} min · next ${hours} h`,
        state: 'DUE',
        note: `requested by ${requesterLabel} ${shownClock}`,
        taskKind: 'collect-sample',
        campaign: { intervalMin, hours },
      };
      const slotCount = Math.min(3, Math.floor((hours * 60) / intervalMin));
      const slotRows: SampleDueItem[] = Array.from({ length: slotCount }, (_, i) => {
        const at = addMinutes(request.requestedAt, intervalMin * (i + 1));
        return {
          id: `campaign-slot-${divergence.id}-${i}`,
          // SA-F19: the banner above already names the stream; a slot row
          // states only the slot it owns, not a fourth repetition of the
          // stream name.
          stream: '',
          slot: clockOf(at),
          state: i === 0 ? 'DUE' : 'UPCOMING',
          taskKind: 'collect-sample',
          continuation: true,
        };
      });
      dueNow = [banner, ...slotRows, ...dueNow];
    } else {
      const item: SampleDueItem = {
        id: `rush-${divergence.id}`,
        stream: streamLabel,
        slot: shownClock,
        state: 'DUE',
        note: `rush · requested by ${requesterLabel} ${shownClock}`,
        taskKind: 'collect-sample',
      };
      dueNow = [...dueNow, item];
    }
  }
  return { dueNow, collectedPending: base.collectedPending, missed: base.missed };
}

/** Count of *distinct streams* represented in `dueNow`, for the section
 * subtitle (S1.2/SA-F7 residue; corrected for SA-F18) -- derived from the
 * computed list, never a hand-typed number. A campaign contributes its
 * banner row (`Campaign · <stream>`) plus up to three nested slot rows for
 * the same stream; normalise the banner's label and drop the slot rows
 * (whose own `stream` is empty, see `continuation`) so the campaign counts
 * once, like every other stream. */
export function dueNowCount(lists: SampleDueLists): number {
  const streams = new Set(
    lists.dueNow
      .filter((item) => !item.continuation)
      .map((item) => (item.stream.startsWith('Campaign · ') ? item.stream.slice('Campaign · '.length) : item.stream)),
  );
  return streams.size;
}

/** A campaign banner's own clause for the Due next line: `campaign every
 *  30 min for 4 h`. Read off the banner row's structured `campaign`, never
 *  parsed out of its slot text — that text is a cadence, not a clock time, so
 *  grouping it with the due slots produced `1 at every 30 min · next 4 h`
 *  (issue #658 round 2). Empty when the due list carries no campaign. */
export function campaignClause(items: readonly SampleDueItem[]): string {
  const campaign = items.find((item) => item.campaign)?.campaign;
  if (!campaign) return '';
  return `campaign every ${campaign.intervalMin} min for ${campaign.hours} h`;
}

// --- polish.P10 (#658): the locked comp `.impeccable/mocks/decision/quality.html` A ---
// Every figure below is a count or a re-arrangement of what the route already
// reads (the register RoleTable, the Samples Due lists, the seeded KPI tiles,
// the arsenic circuit and distribution seeds, the ConcentrateSpec panel model).

export function isQualityTab(value: string | null | undefined): value is QualityTab {
  return value === 'sample-register' || value === 'impurities' || value === 'samples-due';
}

/** The case's own seeded code (ZN-REC, WIT-021, AS-TREND) — the route names a
 *  case the way Advisory does (discrepancy row 252: "ADV-0114" retires). */
export function caseCodeFor(caseId: string): string {
  return CASES.find((row) => row.id === caseId)?.code ?? caseId;
}

export type RegisterRowView = {
  id: string;
  stream: string;
  samplePointId?: string;
  freq: string;
  collected: string;
  lims: string;
  /** The LIMS cell without its `MISS` state word — the slot alone (`10:00`). */
  slot: string;
  state: SampleAdherence | null;
  statusText: string;
  tat: string;
  tier: string;
  method: string;
  caseId?: string;
};

/** A register LIMS cell with the `MISS` state word stripped: the slot itself.
 *  The one rule behind both the register row view and the strip's missed line. */
export function limsSlot(lims: string): string {
  return lims.replace(/^MISS\s*/, '');
}

/** The register grid as rows the card renders (column lookup by header name). */
export function registerRowsView(table: RoleTable): RegisterRowView[] {
  const col = (name: string) => table.columns.indexOf(name);
  const cell = (cells: readonly string[], name: string) => {
    const index = col(name);
    return index >= 0 ? (cells[index] ?? '') : '';
  };
  return table.rows.map((row) => {
    const statusText = cell(row.cells, 'Status');
    const lims = cell(row.cells, 'LIMS');
    return {
      id: row.id,
      stream: cell(row.cells, 'Stream'),
      samplePointId: REGISTER_ROW_SAMPLE_POINT[row.id],
      freq: cell(row.cells, 'Freq'),
      collected: cell(row.cells, 'Last collected'),
      lims,
      slot: limsSlot(lims),
      state: parseSampleAdherence(statusText),
      statusText,
      tat: cell(row.cells, 'TAT'),
      tier: cell(row.cells, 'Tier'),
      method: cell(row.cells, 'Method'),
      caseId: registerStatusCaseId(row.id, statusText),
    };
  });
}

export type QualityChipTone = 'ok' | 'warn' | 'bad' | 'neutral';

// `adherenceTone()` and its DUE-in-info mapping are gone (issue #658 round 2):
// every adherence chip on the route now renders through the shared
// `StateChip` / `SAMPLE_ADHERENCE_CHIP`, so DUE reads the same amber here as
// it does on `/compliance`. The tone union above stays for the non-adherence
// status chips (mass-balance CLOSED / REVIEW, penalty OK / WATCH / OVER LIMIT).

export type SamplePipelineCellId = 'due' | 'pending' | 'lims' | 'missed';

/** Register states each strip cell selects (the rows it highlights). */
export const PIPELINE_CELL_STATES: Readonly<Record<SamplePipelineCellId, readonly SampleAdherence[]>> = {
  due: ['DUE'],
  pending: ['PENDING'],
  lims: ['REG', 'LATE'],
  missed: ['MISS'],
};

/** A comparison line: optional muted prefix, the lead figure in 600, the rest muted. */
export type SamplePipelineLine = { pre?: string; lead?: string; rest: string };

export type SamplePipelineCell = {
  id: SamplePipelineCellId;
  name: string;
  figure: string;
  figureContext: string;
  lines: readonly SamplePipelineLine[];
  actor?: { kind: PipelineActor; label: string };
  /** The case a missed sample discounts, by id and seeded code. */
  caseLink?: { caseId: string; code: string };
};

export type SamplePipelineRead = {
  cells: readonly SamplePipelineCell[];
  registerTotal: number;
  registered: number;
  onTime: number;
  late: number;
  pending: number;
  missed: number;
  dueStreams: number;
};

function clockMinutes(hhmm: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

function kpiTile(id: string): KpiTile | undefined {
  return ROLE_KPIS.find((tile) => tile.id === id);
}

/** Lab TAT comparison from the seeded tile's own target (`Target ≤ 3 h` → `vs ≤ 3 h target`). */
export function labTatRead(): { value: string; comparison: string } {
  const tile = kpiTile('samp-tat');
  const target = (tile?.sub ?? '').replace(/^Target\s*/i, '').trim();
  return { value: tile?.value ?? '—', comparison: target ? `vs ${target} target` : '' };
}

/**
 * The sample pipeline strip (comp A band): due next → collected, not in LIMS →
 * in LIMS today → missed. Due and the pending samples read the Samples Due
 * lists; registered / on time / late / missed are counts of the register's own
 * rows (discrepancy row 251 — the seeded "87.5 % · 12/16" tile contradicts
 * itself and is not drawn).
 */
export function samplePipelineRead(
  registerTable: RoleTable,
  lists: SampleDueLists,
  cursor: string,
): SamplePipelineRead {
  const rows = registerRowsView(registerTable);
  const of = (state: SampleAdherence) => rows.filter((row) => row.state === state);
  const reg = of('REG');
  const late = of('LATE');
  const missedRows = of('MISS');
  const pendingRows = of('PENDING');
  const registered = reg.length + late.length;

  // Due next: group the due-now *clock* rows by slot. A campaign banner's slot
  // text is a cadence ("every 30 min · next 4 h"), not a clock time, so it is
  // held out of the grouping and gives its own clause (issue #658 round 2).
  const dueRows = lists.dueNow.filter((item) => !item.continuation);
  const dueStreams = dueNowCount(lists);
  const campaignLine = campaignClause(dueRows);
  const slots = new Map<string, { count: number; analyser: boolean }>();
  for (const item of dueRows) {
    if (item.campaign) continue;
    const slot = slots.get(item.slot) ?? { count: 0, analyser: false };
    slot.count += 1;
    slot.analyser = slot.analyser || /analyser/i.test(item.note ?? '');
    slots.set(item.slot, slot);
  }
  const slotEntries = [...slots.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  const withCampaign = (rest: string) => (campaignLine ? (rest ? `${rest} · ${campaignLine}` : campaignLine) : rest);
  let dueLine: SamplePipelineLine;
  if (slotEntries.length === 1) {
    const [slot] = slotEntries[0]!;
    const now = clockMinutes(cursor.split('T')[1] ?? cursor);
    const at = clockMinutes(slot);
    const gap = now !== null && at !== null ? at - now : null;
    dueLine = {
      lead: slot,
      rest: withCampaign(gap !== null && gap > 0 ? ` slot · ${gap} min from now` : ' slot'),
    };
  } else {
    dueLine = {
      rest: withCampaign(
        slotEntries
          .map(([slot, read]) => `${read.count} ${read.analyser ? 'on analyser at' : 'at'} ${slot}`)
          .join(' · '),
      ),
    };
  }

  const pendingLine = lists.collectedPending.map((item) => `${item.stream} ${item.slot}`).join(' · ');
  const tat = labTatRead();

  const missedRow = missedRows[0];
  const missedItem = lists.missed[0];
  const missedSlot = missedRow ? missedRow.slot : '';
  const reason = (missedItem?.note ?? '').replace(/^reason:\s*/i, '');
  const missedCaseId = missedRow?.caseId;

  const cells: SamplePipelineCell[] = [
    {
      id: 'due',
      name: 'Due next',
      figure: String(dueStreams),
      figureContext: dueStreams === 1 ? 'stream' : 'streams',
      lines: [dueLine],
      actor: { kind: 'Human', label: 'Operator' },
    },
    {
      id: 'pending',
      name: 'Collected · not in LIMS',
      figure: String(pendingRows.length),
      figureContext: pendingRows.length === 1 ? 'sample' : 'samples',
      lines: [{ rest: pendingLine }],
      actor: { kind: 'Human', label: 'Operator' },
    },
    {
      id: 'lims',
      name: 'In LIMS · today',
      figure: String(registered),
      figureContext: `of ${rows.length} streams`,
      lines: [
        {
          lead: String(reg.length),
          rest: ` on time · ${late.length} late${late.length ? ` (${late.map((row) => row.stream).join(', ')})` : ''}`,
        },
        { pre: 'Lab TAT ', lead: tat.value, rest: tat.comparison ? ` ${tat.comparison}` : '' },
      ],
      actor: { kind: 'System', label: 'LIMS' },
    },
    {
      id: 'missed',
      name: 'Missed',
      figure: String(missedRows.length),
      figureContext: missedRows.length === 1 ? 'sample' : 'samples',
      lines: missedRow
        ? [{ lead: `${missedRow.stream}${missedSlot ? ` ${missedSlot}` : ''}`, rest: reason ? ` · ${reason}` : '' }]
        : [],
      caseLink: missedCaseId ? { caseId: missedCaseId, code: caseCodeFor(missedCaseId) } : undefined,
    },
  ];

  return {
    cells,
    registerTotal: rows.length,
    registered,
    onTime: reg.length,
    late: late.length,
    pending: pendingRows.length,
    missed: missedRows.length,
    dueStreams,
  };
}

/** Pill-tab counts: each tab carries its headline before it is opened. */
export function qualityTabCounts(pipeline: SamplePipelineRead): Record<QualityTab, string> {
  const watch = concentratePenaltyPanelModel().groups.flatMap((group) => group.rows).filter((row) => row.statusLabel === 'WATCH').length;
  return {
    'sample-register': `${pipeline.registered} of ${pipeline.registerTotal}`,
    impurities: `${watch} watch`,
    'samples-due': `${pipeline.dueStreams} due`,
  };
}

export type PanelHeadline = { figure: string; figureContext?: string; lead: string; rest: string };

/** Setpoint panel figure: the seeded compliance tile (82.3 %), its lead term,
 *  and the target the meters' tick draws. */
export function setpointHeadline(): PanelHeadline {
  const tile = kpiTile('samp-sp');
  const target = SETPOINT_IN_RANGE[0]?.marker?.value;
  const lead = (tile?.sub ?? '').split(' · ')[0] ?? '';
  return { figure: tile?.value ?? '—', lead, rest: target !== undefined ? ` · vs ${target}% target` : '' };
}

/** Setpoint target (the tick on every setpoint bar). */
export function setpointTargetPct(): number {
  return SETPOINT_IN_RANGE[0]?.marker?.value ?? 90;
}

/** Reagent panel figure: reagents below the 7-day reorder point, led by the
 *  lowest (`1 below reorder · Aero 5100 · 6 d vs 7-day reorder`). */
export function reagentHeadline(meters: readonly ReagentSupplyMeter[]): PanelHeadline {
  const below = meters.filter((meter) => belowReorder(meter.days));
  const lowest = [...meters].sort((a, b) => a.days - b.days)[0];
  return {
    figure: String(below.length),
    figureContext: 'below reorder',
    lead: lowest?.name ?? '',
    rest: lowest ? ` · ${lowest.days} d vs ${REAGENT_REORDER_DAYS}-day reorder` : '',
  };
}

/** Mass-balance tolerance (the seeded caption's ±3 %) and the diverging bar
 *  scale (twice the tolerance, so an over-tolerance error still draws). */
export const MASS_BALANCE_TOLERANCE_PCT = 3;
export const MASS_BALANCE_SCALE_PCT = MASS_BALANCE_TOLERANCE_PCT * 2;

export type DivergingBarGeometry = { leftPct: number; widthPct: number; bandLeftPct: number; bandWidthPct: number };

export function closureBarGeometry(errorPct: number): DivergingBarGeometry {
  const half = 50;
  const width = Math.min(half, (Math.abs(errorPct) / MASS_BALANCE_SCALE_PCT) * half);
  const band = (MASS_BALANCE_TOLERANCE_PCT / MASS_BALANCE_SCALE_PCT) * half;
  return {
    leftPct: errorPct >= 0 ? half : half - width,
    widthPct: width,
    bandLeftPct: half - band,
    bandWidthPct: band * 2,
  };
}

export type ArsenicCircuitCell = {
  id: string;
  name: string;
  kind: 'arsenic' | 'condition';
  value: string;
  line: string;
  /** Arsenic cells: the 30-day move (`▲ 0.10 pt` · ` / 30 d`), Pb/Ag only. */
  trend?: { lead: string; rest: string };
  /** Arsenic cells: share of the plant's arsenic reporting here. */
  sharePct?: number;
  shareLabel?: string;
  /** Arsenic cells: the concentrate's penalty pill when it is not OK. */
  status?: { label: string; tone: QualityChipTone };
};

const AS_NODE_KPI: Record<string, string> = { 'as-n1': 'as-rom', 'as-n4': 'as-pb', 'as-n6': 'as-zn', 'as-n8': 'as-tails' };
const AS_NODE_SHARE: Record<string, string> = { 'as-n4': 'Pb/Ag conc', 'as-n6': 'Zn conc', 'as-n8': 'Final tails' };
const AS_NODE_CONCENTRATE: Record<string, { group: string; limit: 'Pb' | 'Zn' }> = {
  'as-n4': { group: 'cs-pb', limit: 'Pb' },
  'as-n6': { group: 'cs-zn', limit: 'Zn' },
};

/** Pb/Ag As move over the 30-day series (last − first). */
export function arsenicPbMove30d(): number {
  const series = ARSENIC_TREND_30D.series.find((row) => row.name === 'Pb/Ag conc');
  if (!series || series.points.length < 2) return 0;
  return series.points[series.points.length - 1]! - series.points[0]!;
}

/**
 * The nine-node arsenic circuit as the Impurities strip (comp A). The four
 * arsenic-bearing nodes carry the value the KPI row carried, the smelter limit,
 * and the share the distribution meters carried (89 / 7 / 4), so each figure
 * is said once; the five process nodes keep their condition.
 */
export function arsenicCircuitCells(): ArsenicCircuitCell[] {
  const penaltyGroups = concentratePenaltyPanelModel().groups;
  return ARSENIC_CIRCUIT_NODES.map((node) => {
    const kpiId = AS_NODE_KPI[node.id];
    if (!kpiId) {
      return { id: node.id, name: node.name, kind: 'condition', value: node.line1, line: node.line2 };
    }
    const value = node.line1.replace(/\s*As$/, '');
    const concentrate = AS_NODE_CONCENTRATE[node.id];
    let line = kpiTile(kpiId)?.sub ?? '';
    if (concentrate) line = `of ${arsenicContractLimit(concentrate.limit).toFixed(2)}% smelter limit`;
    if (node.id === 'as-n8') line = 'to the dam';
    const shareRow = AS_NODE_SHARE[node.id]
      ? AS_DISTRIBUTION_BY_PRODUCT.find((row) => row.label === AS_NODE_SHARE[node.id])
      : undefined;
    const sharePct = shareRow ? shareRow.value : node.id === 'as-n1' ? 100 : undefined;
    const shareLabel = sharePct === undefined ? undefined : sharePct === 100 ? '100% of As' : `~${sharePct}% of As`;
    let status: ArsenicCircuitCell['status'];
    if (concentrate) {
      const row = penaltyGroups.find((group) => group.id === concentrate.group)?.rows.find((r) => r.element === 'As');
      if (row && row.tone !== 'ok') status = { label: row.statusLabel, tone: row.tone === 'bad' ? 'bad' : row.tone === 'warn' ? 'warn' : 'neutral' };
    }
    const move = arsenicPbMove30d();
    return {
      id: node.id,
      name: node.name,
      kind: 'arsenic',
      value,
      line,
      trend: node.id === 'as-n4' ? { lead: `${move >= 0 ? '▲' : '▼'} ${Math.abs(move).toFixed(2)} pt`, rest: ' / 30 d' } : undefined,
      sharePct,
      shareLabel,
      status,
    };
  });
}

export type SampleDueRowView = {
  id: string;
  stream: string;
  slot: string;
  state: SampleAdherence;
  samplePointId?: string;
  detail?: string;
  caseId?: string;
  continuation?: boolean;
};

/** Samples Due rows with their sample-point id (comp: B's SA ids); the OSA
 *  note loses its SA prefix because the id is already on the row. */
export function samplesDueRowsView(items: readonly SampleDueItem[]): SampleDueRowView[] {
  return items.map((item) => {
    const samplePointId = DUE_ROW_SAMPLE_POINT[item.id];
    const detail = item.note && samplePointId ? item.note.replace(new RegExp(`^${samplePointId}\\s*·\\s*`), '') : item.note;
    return {
      id: item.id,
      stream: item.stream,
      slot: item.slot,
      state: item.state,
      samplePointId,
      detail: detail?.replace(/^reason:\s*/i, 'reason: '),
      caseId: item.caseId,
      continuation: item.continuation,
    };
  });
}

/** The case the missed card's chip opens: the first missed row that carries
 *  one. Lives here so the card reads a transform, not a local `find`
 *  (issue #658 round 2). */
export function missedCaseIdIn(rows: readonly SampleDueRowView[]): string | undefined {
  return rows.find((row) => row.caseId)?.caseId;
}
